import { PARAMETER_INPUT_TYPE } from '../constants'

export type ParameterKey =
    | 'projectGroup'
    | 'owner'
    | 'buildingType'
    | 'constructionType'
    | 'contractType'
    | 'sustainability'
    | 'geographicRegion'
    | 'seismicZone'
    | 'windZone'
    | 'city'
    | 'provinceState'
    | 'cityRegionCountry'

export type ParameterToggleState = Record<ParameterKey, boolean>
export type ParameterSelectedState = Record<ParameterKey, string[]>
export type ParameterOptionsMap = Record<ParameterKey, string[]>

/** How a parameter's value is entered. Defaults to 'select' when omitted. */
export type ParameterInputType = 'select' | 'multiSelect' | 'textField'

export const parameterConfig: {
    key: ParameterKey
    label: string
    inputType?: ParameterInputType
}[] = [
    { key: 'projectGroup', label: 'Project Group' },
    { key: 'owner', label: 'Owner' },
    { key: 'buildingType', label: 'Building Type' },
    { key: 'constructionType', label: 'Construction Type' },
    { key: 'contractType', label: 'Contract Type' },
    { key: 'sustainability', label: 'Sustainability' },
    { key: 'geographicRegion', label: 'Geographic Region' },
    { key: 'seismicZone', label: 'Seismic Zone' },
    { key: 'windZone', label: 'Wind Zone' },
    { key: 'city', label: 'City', inputType: PARAMETER_INPUT_TYPE.TEXT_FIELD },
    { key: 'provinceState', label: 'Province/State' },
    { key: 'cityRegionCountry', label: 'City/Region/Country' },
]

export const emptyParameterToggles: ParameterToggleState = {
    projectGroup: false,
    owner: false,
    buildingType: false,
    constructionType: false,
    contractType: false,
    sustainability: false,
    geographicRegion: false,
    seismicZone: false,
    windZone: false,
    city: false,
    provinceState: false,
    cityRegionCountry: false,
}

export const emptyParameterSelected: ParameterSelectedState = {
    projectGroup: [],
    owner: [],
    buildingType: [],
    constructionType: [],
    contractType: [],
    sustainability: [],
    geographicRegion: [],
    seismicZone: [],
    windZone: [],
    city: [],
    provinceState: [],
    cityRegionCountry: [],
}

export type BPMode = 'none' | 'parameter' | 'global'

export interface BPStagedConfig {
    mode: BPMode
    projectGroup?: string
    city?: string
    provinceState?: string
    parameterSelected: ParameterSelectedState
}

export const initialBPStagedConfig: BPStagedConfig = {
    mode: 'none',
    projectGroup: undefined,
    parameterSelected: emptyParameterSelected,
}

export type BPTipType = 'parameter' | 'global'

/** Audit-log action the tip card represents. Mirrors BE BestPracticeAuditAction enum. */
export type BPTipAction = 'SET' | 'MODIFY' | 'REMOVE' | 'RESYNC'

export interface BPTipAuthor {
    /** Display name — resolved from profileInfo (first + last), email, then userId as last resort. */
    name: string
}

/**
 * Per-parameter values shown on a parameter-type tip card.
 * Each key is optional so the card only renders the parameters
 * that were actually tagged.
 */
export type BPTipParameters = Partial<Record<ParameterKey, string>>

export interface BPVersionElement {
    id?: string | null
    text?: string | null
    type?: string | null
    placeholderText?: string | null
    imperialUnits?: string | null
    metricUnits?: string | null
    hyperlinkUrl?: string | null
    elements?: BPVersionElement[] | null
}

export interface BPVersion {
    id: string
    timestamp: string
    modifiedBy?: string | null
    isHidden: boolean
    elements?: BPVersionElement[] | null
}

export interface BestPracticeTip {
    id: string
    /** The audit event this card represents (Set / Modify / Remove). */
    action: BPTipAction
    type: BPTipType
    author: BPTipAuthor
    /** ISO 8601 timestamp of the audit event (Set/Modify/Remove). */
    occurredAt: string
    /** ID of the content item this audit entry is scoped to. */
    contentItemId?: string | null
    /** Fallback content item ID when contentItemId no longer exists in the workspace. */
    appliedContentItemId?: string | null
    /** Required when type === 'parameter', undefined when type === 'global'. */
    parameters?: BPTipParameters
    /** Human-readable diff text (populated by BE only for Modify entries). */
    changeDescription?: string
    /** All versions for the BP, newest first. Null for Remove entries. */
    version?: BPVersion | null
    /** ID of the best practice this audit entry is scoped to. */
    bestPracticeId: string
}
